// frontend/src/components/ui/layout/Footer.jsx
import { Link } from "react-router-dom";
import { Button } from "./../button";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Send,
  Briefcase,
  Users,
  Building2,
} from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    forJobSeekers: [
      { label: "Browse Jobs", to: "/" },
      { label: "My Applications", to: "/applier/dashboard" },
      { label: "Saved Jobs", to: "/applier/dashboard?tab=saved" },
    ],
    forEmployers: [
      { label: "Post a Job", to: "/recruiter/post-job" },
      { label: "Recruiter Dashboard", to: "/recruiter/dashboard" },
      { label: "Manage Applications", to: "/recruiter/applications" },
    ],
    company: [
      { label: "About Us", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Blog", to: "/blog" },
    ],
    legal: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Cookie Policy", to: "/cookie-policy" },
    ],
  };

  return (
    <footer className="bg-gradient-to-b from-muted/30 to-muted/50 border-t border-border w-full">
      <div className="w-full px-6 sm:px-12 lg:px-20 py-12">
        {/* Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-16">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="flex items-center space-x-2 group">
              <img
                src="/shebeka_logo.png"
                alt="Shebeka Logo"
                className="h-8 w-auto transition-transform group-hover:scale-105 dark:hidden"
              />
              <img
                src="/shebeka_logo_dark.png"
                alt="Shebeka Logo"
                className="h-8 w-auto transition-transform group-hover:scale-105 hidden dark:block"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Connecting Ethiopian talent with opportunity. Your dream job is just one
              click away.
            </p>

            {/* Social Links */}
            <div className="flex flex-wrap gap-2">
              {[
                { Icon: Facebook, href: "https://facebook.com/shebeka", label: "Facebook" },
                { Icon: Twitter, href: "https://twitter.com/shebeka", label: "Twitter" },
                { Icon: Linkedin, href: "https://linkedin.com/company/shebeka", label: "LinkedIn" },
                { Icon: Instagram, href: "https://instagram.com/shebeka", label: "Instagram" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  aria-label={label}
                >
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 hover:text-primary hover:bg-primary/10 transition-colors"
                  >
                    <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  </Button>
                </a>
              ))}
            </div>

            {/* Telegram Channel */}
            <a
              href="https://t.me/shebeka_job"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
            >
              <Send className="w-4 h-4" />
              Join Telegram Channel
            </a>
          </div>

          {/* For Job Seekers */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-primary" />
              For Job Seekers
            </h3>
            <ul className="space-y-2.5">
              {footerLinks.forJobSeekers.map((link, index) => (
                <li key={`${link.to}-${index}`}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Employers */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              For Employers
            </h3>
            <ul className="space-y-2.5">
              {footerLinks.forEmployers.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Company
            </h3>
            <ul className="space-y-2.5">
              {footerLinks.company.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2.5">
              {footerLinks.legal.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info Section */}
        <div className="mt-10 pt-8 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-4">Contact Us</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Mail className="w-4 h-4 text-primary shrink-0" />
              <a href="mailto:support@shebeka.com">support@shebeka.com</a>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Phone className="w-4 h-4 text-primary shrink-0" />
              <a href="tel:+251911234567">+251 91 123 4567</a>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              <span>Addis Ababa, Ethiopia</span>
            </div>
          </div>
        </div>

        {/* Divider + Copyright */}
        <div className="mt-10 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-center md:text-left text-sm text-muted-foreground">
              &copy; {currentYear} Shebeka. All rights reserved.
            </p>
            <p className="text-center md:text-right text-sm">
              <span className="text-muted-foreground">Developed with </span>
              <span className="text-red-500">❤</span>
              <span className="text-muted-foreground"> by </span>
              <a
                href="https://allcandevelopmentcenter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 transition-colors font-semibold"
              >
                <Send className="w-3.5 h-3.5" />
                <span className="underline decoration-primary decoration-1 underline-offset-2">
                  Allcan Development Center
                </span>
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
